import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as moment from 'moment';
import { Globals } from '../../../services/globals.service';
import { UserService } from '../../../services/user.service';
import { LiveDataService } from '../../../services/live-data.service';
import { MockDataService } from '../../../services/mock-data.service';
import { ApplicationService } from '../../../services/application.service';
import { BaseComponent } from '../../../classes/base-component';
import { LiveChartComponent } from '../../../charts/live-chart/live-chart.component';
import { ElectricityService } from '../../../services/electricity.service';
import { TILE_TYPE, TileService } from '../../../services/tile.service';
import { HomestateService } from '../../../services/homestate.service';
import { TrackAnalyticsService } from '../../../services/track-analytics.service';
import { InitializationService } from '../../../services/initialization.service';
import { of, Subscription, throwError, timer } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { RemainingTimeIndicatorComponent } from '../../../components/remaining-time-indicator/remaining-time-indicator.component';
import { HappyHourService } from '../../../services/happy-hour.service';

@Component({
    selector: 'app-live-tile',
    templateUrl: './live-tile.component.html',
    styleUrls: ['./live-tile.component.scss'],
    providers: [Globals],
})
export class LiveTileComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
    private readonly type: TILE_TYPE = TILE_TYPE.LIVE;
    private mockDataIntervalSub: Subscription = null;
    private currentDataset: Array<PowerValue> = [];

    private happyHourFrom = null;
    private happyHourTo = null;

    currentConsumption = 0;
    valueUnit: 'Watt' | 'Wh' = 'Watt';
    status = { trend: 0, noZone: true };

    isNotRealtime = false;
    userHasHappyHour = false;

    isVisionUser = false;

    regularSeriesColor = '#47A7D8';
    feedinSeriesColor = '#ffc300';
    happyHourSeriesColor = '#143C8C';

    @ViewChild('happyHourIndicator') hhIndicator: RemainingTimeIndicatorComponent;
    @ViewChild('liveChart', { static: true }) liveChart: LiveChartComponent;

    constructor(
        private globals: Globals,
        private mockDataService: MockDataService,
        private userService: UserService,
        private liveData: LiveDataService,
        private application: ApplicationService,
        private analytics: TrackAnalyticsService,
        private electricity: ElectricityService,
        private tiles: TileService,
        private homeState: HomestateService,
        private happyHour: HappyHourService,
        private initialization: InitializationService
    ) {
        super();
    }

    ngOnInit() {
        this.valueUnit = this.userService.isERNAUser() ? 'Wh' : 'Watt';
        this.liveChart.setUnit(this.valueUnit);
    }

    ngAfterViewInit() {
        this.liveChart.reset();
        if (this.userService.isERNAUser()) {
            this.liveChart.updateZoomLevel(60 * 30 * 6 * 1000, '%H:%M');
        } else {
            this.liveChart.updateZoomLevel(60 * 1000, '%H:%M');
        }
        this.initializeComponent();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.mockDataIntervalSub) {
            this.mockDataIntervalSub.unsubscribe();
            this.mockDataIntervalSub = null;
        }
    }

    onTileClicked(): void {
        this.detailEntered();
        this.tiles.openDetailView(this.type);
    }

    detailEntered() {
        if (!this.globals.getFirstDetailsViewed()) {
            this.trackFirstDetailView();
        }
        this.globals.setFirstDetailsViews();
        this.trackDetailsEntered();
    }

    currentConsumptionFormatted(): string {
        return Math.abs(this.currentConsumption).toLocaleString('de-DE');
    }

    checkIsVisionUser(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.initialization.getWithCache().subscribe(
                (data) => {
                    if ('product_name' in data) {
                        this.isVisionUser = data.product_name.toLowerCase().includes('vision');
                    }
                    resolve();
                },
                (error) => {
                    reject(error);
                }
            );
        });
    }

    /**
     * Initializes the Component with requests made
     */
    private initializeComponent(): void {
        // initialize chart for demo mode
        if (this.application.isDemoMode()) {
            this.initializeMockData();
            return;
        }

        if (this.userService.isEnviamUser()) {
            if (this.userService.getActiveUserProvider().toLowerCase() !== 'opto') {
                this.initializeHappyHour();
            }
        }

        this.initPowerApiConnection();

        // homestate status live update
        this.initHomeStateStatus();
    }

    private initPowerApiConnection(): void {
        this.liveData.startCurrentConsumptionUpdate(true);
        const liveS = this.liveData.onLiveConsumptionReceived
            .pipe(
                mergeMap((values) => (!values ? of(null) : of(values))),
                catchError(() => of(null))
            )
            .subscribe(
                (res) => {
                    try {
                        if (!res) {
                            return;
                        }
                        const data = res.results as Array<PowerValue>;
                        if (data.length === 0) {
                            this.liveChart.showLoadingState(true);
                            return;
                        }

                        // filter by the last 5 minutes
                        const filtered = this.filterValuesForTimeframe(data);

                        // update chart
                        this.updateChartWithData(filtered);

                        // find last value
                        const fiveMinCopy = filtered.slice().reverse();
                        let latest = fiveMinCopy.find((el) => 'power' in el);
                        if (latest) {
                            this.currentConsumption = latest.power;
                        } else {
                            const fullHourCopy = data.slice().reverse();
                            latest = fullHourCopy.find((el) => 'power' in el);
                            this.currentConsumption = latest.power;
                        }
                        const currentConsumptionFixed = this.currentConsumption.toFixed(0);
                        this.currentConsumption = parseInt(currentConsumptionFixed, 10);
                    } catch (e) {
                        console.log('Error: Initializing power based API connection.', e);
                    }
                },
                () => null
            );
        this.addSub(liveS);
    }

    private initHomeStateStatus(): void /**/ {
        const homeS = this.homeState.onHomestateInfo.subscribe((data) => {
            try {
                if (!data) {
                    return;
                }
                if (data.current_zone === 4) {
                    this.status.noZone = true;
                    return;
                }
                this.status.noZone = false;
                this.status.trend = data.current_zone;
            } catch (e) {
                console.log('Error: Extracting homestate info.', e);
            }
        });
        this.addSub(homeS);
        this.homeState.startLiveUpdateForBundledInfo();
    }

    private updateChartWithData(dataset: Array<PowerValue>): void {
        const fedEnergy = [];
        for (const el of dataset) {
            const element = {
                power: el.power < 0 ? Math.abs(el.power) : null,
                timestamp: el.timestamp,
            };
            fedEnergy.push(element);
        }

        const consumedEnergy = [];
        const happyHourEnergy = [];
        for (const el of dataset) {
            const element = {
                power: el.power >= 0 ? el.power : null,
                timestamp: el.timestamp,
            };
            if (this.userHasHappyHour) {
                const date = moment(el.timestamp).toDate();
                if (date >= this.happyHourFrom && date <= this.happyHourTo) {
                    happyHourEnergy.push(element);
                } else {
                    consumedEnergy.push(element);
                }
            } else {
                consumedEnergy.push(element);
            }
        }

        this.currentDataset = dataset;
        this.liveChart.showLoadingState(false);
        this.liveChart.addNewSeries(consumedEnergy, 'power', 3, {
            zindex: 0,
            isTileChart: true,
            color: this.regularSeriesColor,
        });
        this.liveChart.addNewSeries(fedEnergy, 'power', 0, {
            zindex: 1,
            isTileChart: true,
            color: this.feedinSeriesColor,
        });

        if (this.userHasHappyHour) {
            this.liveChart.addNewSeries(happyHourEnergy, 'power', 0, {
                zindex: 2,
                isTileChart: true,
                color: this.happyHourSeriesColor,
            });
        }
    }

    private filterValuesForTimeframe(values: Array<PowerValue>): any {
        const start = moment().subtract(5, 'minutes');
        return values.filter((el) => {
            const ts = moment(el.timestamp);
            return ts >= start && ts <= moment();
        });
    }

    private initializeMockData(): void {
        this.mockDataIntervalSub = timer(0, 10000).subscribe(() => this.getMockConsumption());
    }

    private getMockConsumption(): void {
        const offset = 5;
        const level = 1;
        const limit = 0;
        const interval = 1;

        const s = this.mockDataService
            .getLiveData(offset, level, limit, interval)
            .subscribe((response) => {
                try {
                    this.updateChartWithData(response);

                    // determine current consumption value
                    const currentConsumption = response.last().power;
                    const diff = currentConsumption - Math.floor(currentConsumption);
                    if (diff < 0.5) {
                        this.currentConsumption = Math.floor(currentConsumption);
                        this.currentConsumption = Math.floor(currentConsumption);
                    } else {
                        this.currentConsumption = Math.ceil(currentConsumption);
                    }
                } catch (e) {}
            });
        this.addSub(s);
    }

    /**
     * Request whether the user participates in the happy hour program
     */
    private async initializeHappyHour(): Promise<void> {
        try {
            await this.checkIsVisionUser();

            if (this.isVisionUser) {
                this.userHasHappyHour = false;
                return;
            }

            const s = this.happyHour
                .userHasHappyHour()
                .pipe(
                    mergeMap((participation) => {
                        if (!participation) {
                            return throwError(() => new Error('User does not have Happy Hour'));
                        }
                        this.userHasHappyHour = participation;
                        const d = moment();
                        return this.happyHour.requestSchedule(d);
                    }),
                    mergeMap((res) => {
                        this.happyHourFrom = res.currentStart;
                        this.happyHourTo = res.currentEnd;
                        return of(true);
                    })
                )
                .subscribe(
                    () => {
                        if (this.hhIndicator) {
                            this.hhIndicator.setStartTime(this.happyHourFrom, this.happyHourTo);
                        }
                        this.liveChart.addPlotBand(this.happyHourFrom, this.happyHourTo);
                        this.updateChartWithData(this.currentDataset);
                    },
                    () => s.unsubscribe()
                );
        } catch (error) {
            console.error('Error determining if user is Vision User:', error);
        }
    }

    private trackDetailsEntered(): void {
        this.analytics.trackEvent({
            action: 'dashboard_tile_tapped',
            properties: {
                category: 'Tiles',
                label: 'Tile: Live',
            },
        });
    }

    private trackFirstDetailView(): void {
        this.analytics.trackEvent({
            action: 'first_detail_view',
            properties: {
                category: 'Screens',
                label: 'Screen: Live-Details',
            },
        });
    }
}

export interface PowerValue {
    power: number;
    timestamp: Date;
}
